import React, { useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import { Backdrop, CircularProgress, Typography } from '@mui/material';
import ActionButton from '../../framework/forms/helpercomponents/buttons/ActionButton';
import RefreshButton from '../../framework/forms/helpercomponents/buttons/RefreshButton';
import { useTranslation } from '../../locale/useTranslation';
import { ENTITYNAME, TOAST_TYPES } from '../../framework/constant/constant';
import * as API from '../../framework/API/api';
import { ProgressBar } from '@progress/kendo-react-progressbars';
import ToastNotification from "../../components/ToastNotification";

const IngestForm = (props) => {

    const { entityName, onRefresh } = props;

    const lang = useTranslation();
    const fileRef = useRef(null);

    const [fileName, setFileName] = useState("");
    const [columns, setColumns] = useState([]);
    const [gridData, setGridData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [ingestLoading, setIngestLoading] = useState(false);

    const [showProgressBar, setShowProgressBar] = useState({ okButton: false, progressBarPopup: false });
    const [progressData, setProgressData] = useState({ message: '', progress: 0 });
    const [isUpdateValue, setIsUpdateValue] = useState(false);

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setFileName(file.name);
        setLoading(true);

        try {
            const buffer = await file.arrayBuffer();
            const wb = XLSX.read(buffer, { type: "array" });
            const firstSheet = wb.SheetNames[0];

            const worksheet = wb.Sheets[firstSheet];
            const rows = XLSX.utils.sheet_to_json(worksheet, {
                header: 1,
                defval: ""
            });

            const headerIndex = 0;
            const headers = rows[headerIndex].map(h =>
                String(h).replace(/\s*\*+\s*$/, "").trim()
            );

            const dynamicColumns = headers.map(h => ({
                field: h,
                title: h,
                width: "180px"
            }));

            const excelDateToJSDate = (serial) => {
                if (!serial || isNaN(serial)) return serial;

                const utc_days = Math.floor(serial - 25569);
                const utc_value = utc_days * 86400;
                const date_info = new Date(utc_value * 1000);

                const day = String(date_info.getUTCDate()).padStart(2, '0');
                const month = String(date_info.getUTCMonth() + 1).padStart(2, '0');
                const year = date_info.getUTCFullYear();

                return `${day}/${month}/${year}`;
            };

            const dateFields = [
                "Order Date",
                "From",
                "To",
                "From Date",
                "To Date",
                "Period From",
                "Period To",
                "Date"
            ];

            const data = rows.slice(headerIndex + 1).filter(row => row.some(cell => String(cell).trim() !== "")).map(row => {
                let obj = {};
                headers.forEach((h, i) => {
                    let value = row[i];

                    if (dateFields.includes(h)) {
                        value = excelDateToJSDate(value);
                    }

                    obj[h] = value;
                });
                return obj;
            });

            setColumns(dynamicColumns);
            setGridData(data);

        } catch (err) {
            ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: "Unable to read excel file" });
        } finally {
            setLoading(false);
        }
    };

    const onClear = () => {
        setFileName("");
        setColumns([]);
        setGridData([]);
        setShowProgressBar({ okButton: false, progressBarPopup: false });
        setIsUpdateValue(false);
        fileRef.current.value = null;
    };

    // Status cell renderer
    const statusCell = (props) => (
        <td className='text-center'>
            <span className={`badge fs-6 p-2 ${props.dataItem.status === 'success' ? 'bg-success text-white' : 'bg-danger text-white'}`}>
                {props.dataItem.status?.toUpperCase()}
            </span>
        </td>
    );

    const messageCell = (props) => (
        <td style={{
            padding: '12px 8px',
            wordBreak: 'break-word',
            maxWidth: '300px'
        }}>
            {props.dataItem.message}
        </td>
    );

    const onIngest = async () => {

        if (fileName === '') {
            ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: "Please select file" });
            return;
        }

        setIngestLoading(true);
        setShowProgressBar({ okButton: false, progressBarPopup: true });
        setProgressData({ message: 'Starting import...', progress: 0 });

        try {
            const finalDataToSave = [...gridData];
            const finalChuckedSize = 50;
            const totalChunks = Math.ceil(finalDataToSave.length / finalChuckedSize);

            let dataRowIndex = 0;
            let onSaveResponse = true;

            while (dataRowIndex < finalDataToSave.length && onSaveResponse) {

                const currentProgress = Math.round(((dataRowIndex + 1) / finalDataToSave.length) * 100);
                setProgressData({
                    message: `Processing chunk ${Math.floor(dataRowIndex / finalChuckedSize) + 1}/${totalChunks}...`,
                    progress: currentProgress
                });

                const chunkData = [...finalDataToSave.slice(dataRowIndex, dataRowIndex + finalChuckedSize)];

                const payload = {
                    data: chunkData,
                    chunkIndex: Math.floor(dataRowIndex / finalChuckedSize) + 1,
                    totalChunks: totalChunks,
                    isUpdateValue: isUpdateValue
                };

                let res = { success: false, data: [], message: 'Entity not found' };
                switch (entityName) {
                    case ENTITYNAME.Agency:
                        res = await API.saveAgency(payload);
                        break;
                    case ENTITYNAME.Client:
                        res = await API.saveClient(payload);
                        break;
                    case ENTITYNAME.Campaign:
                        res = await API.saveCampaign(payload);
                        break;
                    default:
                        break;
                }
                console.log(res);

                const chunkResults = res.data || [];

                chunkResults.forEach(result => {
                    const originalRowIndex = dataRowIndex + chunkResults.indexOf(result);
                    const key = entityName === ENTITYNAME.Campaign ? "CampaignNo" : "Name";

                    if (originalRowIndex < gridData.length && (result?.[key] === gridData[originalRowIndex]?.[key])) {
                        gridData[originalRowIndex] = {
                            ...gridData[originalRowIndex],
                            status: result.status,
                            message: result.message
                        };
                    }
                });

                console.log([...gridData]);
                setGridData([...gridData]);

                if (!res.success) {
                    onSaveResponse = false;
                }

                dataRowIndex = dataRowIndex + finalChuckedSize;
            }

            if (onSaveResponse) {
                setProgressData({ message: 'Process completed successfully!', progress: 100 });
                setShowProgressBar({ okButton: true, progressBarPopup: true });
            }

        } catch (err) {
            setProgressData({ message: 'Process failed!', progress: 0 });
            setShowProgressBar({ okButton: true });
        } finally {
            setIngestLoading(false);
            onRefresh();
        }
    };

    return (
        <div>
            {/* FILE INPUT */}
            <div className="mb-3 d-flex align-items-center gap-2">
                {/* File name display */}
                <input
                    disabled={ingestLoading}
                    value={fileName}
                    className="form-control form-control-sm"
                    style={{ width: "200px" }}
                    placeholder="No file selected"
                    readOnly
                />
                <button
                    className="k-button k-button-solid-secondary ml-1"
                    style={{ borderRadius: "5px", width: "30px", height: "30px" }}
                    disabled={ingestLoading}
                    onClick={() => fileRef.current.click()}
                    title="Browse file"
                >
                    <i className="fa fa-ellipsis-h" />
                </button>

                <input
                    ref={fileRef}
                    type="file"
                    hidden
                    accept=".xls,.xlsx"
                    disabled={ingestLoading}
                    onChange={handleFileChange}
                />

                <button
                    className="k-button k-button-solid ml-1"
                    style={{ borderRadius: "5px", width: "30px", height: "30px", backgroundColor: "#C82333", color: "white", border: "none" }}
                    disabled={ingestLoading}
                    onClick={onClear}
                    title="Clear file"
                >
                    <i className="fa fa-times" />
                </button>

                <ActionButton
                    title="Ingest"
                    name="Ingest"
                    btnColor="success"
                    isLoading={ingestLoading}
                    onClick={onIngest}
                />

                <input
                    type="checkbox"
                    className='ml-2'
                    checked={isUpdateValue}
                    onChange={(e) => setIsUpdateValue(e.target.checked)}
                /><label className='ml-1'>{lang.update_label}</label>
            </div>

            {showProgressBar.progressBarPopup && (
                <div id='myProgressBar'>
                    <div className='myProgressBarBox'>
                        <ProgressBar
                            label={(props) => <strong>{props.value}%</strong>}
                            labelPlacement='center'
                            progressStyle={{ backgroundColor: "green" }}
                            value={progressData.progress}
                            style={{ width: "360px" }}
                        />
                        <div className='message'>
                            <p>{progressData.message}</p>
                        </div>
                        {showProgressBar.okButton && <button onClick={() => { setShowProgressBar({ ...showProgressBar, progressBarPopup: false }); }} style={{ width: "50px" }} className='k-button k-button-md k-rounded-md k-button-solid k-button-solid-base bg-success text-white' >Ok</button>}
                    </div>
                </div>
            )}

            {/* GRID */}
            <div style={{ height: "500px" }}>
                <Grid data={gridData} style={{ height: "500px" }} resizable sortable>
                    <GridColumn
                        title="Sno."
                        width="60px"
                        cell={(props) => (
                            <td style={{ padding: '12px 8px', fontWeight: '500' }}>
                                {props.dataIndex + 1}
                            </td>
                        )}
                    />
                    {gridData?.some(r => r?.status) && <GridColumn field="status" title="Status" width="120px" cell={statusCell} />}
                    {gridData?.some(r => r?.message) && <GridColumn field="message" title="Message" width="250px" cell={messageCell} />}
                    {columns.map(col => (
                        <GridColumn
                            key={col.field}
                            field={col.field}
                            title={col.title}
                            width={col.width || "160px"}
                        />
                    ))}
                </Grid>
            </div>

            {/* SUMMARY */}
            <div className="mt-3 d-flex">
                <div className='mr-1'>Total: {gridData.length}</div> | <div className='ml-1 mr-1'>Success: {gridData.filter(r => r.status === 'success').length}</div> | <div className='ml-1'>Failed: {gridData.filter(r => r.status === 'failure').length}</div>
            </div>

            {/* BACKDROP */}
            {loading && (
                <Backdrop open sx={{ color: "#fff", zIndex: 10 }}>
                    <CircularProgress />
                    <Typography sx={{ ml: 2 }}>Reading Excel…</Typography>
                </Backdrop>
            )}
        </div>
    );
};

export default IngestForm;